import { useRef, useState, useCallback, useEffect } from 'react';
import type { RefObject } from 'react';

export interface UseTruncationDetectionOptions {
  containerRef?: RefObject<HTMLElement | null>;
  deps?: readonly unknown[];
}

export interface UseTruncationDetectionResult<T extends HTMLElement> {
  isTruncated: boolean;
  ref: (element: T | null) => void;
}

/**
 * Detects if an element's text is truncated and observes size changes.
 *
 * @param options Optional config; pass `containerRef` to observe a stable ancestor for resizing.
 * Pass `deps` (e.g. text content) to re-check when content changes without a layout resize.
 * @returns { isTruncated, ref } - Boolean indicating truncation and a ref setter for the element.
 */
export const useTruncationDetection = <T extends HTMLElement>(
  options: UseTruncationDetectionOptions = {}
): UseTruncationDetectionResult<T> => {
  const { containerRef, deps = [] } = options;
  const observerRef = useRef<ResizeObserver | null>(null);
  const measureRef = useRef<T | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = useCallback((): void => {
    const el = measureRef.current;

    if (el) {
      setIsTruncated(isElementTruncated(el));
    }
  }, []);

  const disconnectObserver = useCallback((): void => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  }, []);

  // containerRef: observe consumer RefObject + text node; deps re-check (overflow need not resize).
  // Standalone: deps-only re-check — ResizeObserver is attached in ref below.
  useEffect((): void | (() => void) => {
    if (containerRef) {
      if (!containerRef.current) {
        return undefined;
      }

      checkTruncation();

      disconnectObserver();

      const observer = new ResizeObserver(checkTruncation);

      observer.observe(containerRef.current);

      const measureEl = measureRef.current;

      if (measureEl) {
        observer.observe(measureEl);
      }

      observerRef.current = observer;

      return disconnectObserver;
    }

    checkTruncation();

    return undefined;
  }, [containerRef, checkTruncation, disconnectObserver, ...deps]);

  // Text element ref: store measureRef and check on mount. Observer lives here only without containerRef.
  const ref = useCallback(
    (element: T | null) => {
      if (!containerRef) {
        disconnectObserver();
      }

      measureRef.current = element;

      if (!element) {
        return;
      }

      checkTruncation();

      if (containerRef) {
        return;
      }

      observerRef.current = new ResizeObserver(checkTruncation);
      observerRef.current.observe(element);
    },
    [containerRef, checkTruncation, disconnectObserver]
  );

  return { isTruncated, ref };
};

function isElementTruncated(el: HTMLElement): boolean {
  if (el.scrollWidth > el.clientWidth) {
    return true;
  }

  if (!el.firstChild) {
    return false;
  }

  const range = document.createRange();

  range.selectNodeContents(el);

  if (typeof range.getBoundingClientRect !== 'function') {
    return false;
  }

  const textWidth = range.getBoundingClientRect().width;
  const elementWidth = el.getBoundingClientRect().width;

  return textWidth > elementWidth;
}
